import { createContext, useContext, useState, useCallback } from "react";
import { InstitutionWithAddresses } from "../types/institution.types";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useToast } from "../components/toast";
import { InstitutionsAPI } from "../APIs/institutions.api";

interface IInstitutionContextProps {
  institutions: InstitutionWithAddresses[];
  getInstitutions: () => void;
  error: string | null;
  isLoading: boolean;
  handleError: (
    message: string,
    options?: { persist?: boolean; toast?: boolean; toastDuration?: number }
  ) => void;
  addToast: (message: any) => void;
  addInstitution: (newInstitution: InstitutionWithAddresses) => void;
  updateInstitution: (updatedInstitution: InstitutionWithAddresses) => void;
  handleDeleteInstitution: (
    institution: InstitutionWithAddresses
  ) => Promise<void>;
}

const InstitutionsContext = createContext<IInstitutionContextProps | undefined>(
  undefined
);

export const InstitutionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [institutions, setInstitutions] = useState<InstitutionWithAddresses[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const { error, handleError } = useErrorHandler();
  const { addToast } = useToast();

  const getInstitutions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await InstitutionsAPI.getInstitutions();
      if (response.statusCode === 200 && response.data) {
        setInstitutions(response.data);
      } else {
        handleError(response.message);
      }
    } catch (error) {
      handleError("Error al obtener las barras pesqueras");
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  const addInstitution = useCallback(
    (newInstitution: InstitutionWithAddresses) => {
      setInstitutions((prevInstitutions) => [
        ...prevInstitutions,
        newInstitution,
      ]);
    },
    []
  );

  const updateInstitution = useCallback(
    (updatedInstitution: InstitutionWithAddresses) => {
      setInstitutions((prevInstitutions) =>
        prevInstitutions.map((institution) =>
          institution.institution.id === updatedInstitution.institution.id
            ? updatedInstitution
            : institution
        )
      );
    },
    []
  );

  const deleteInstitution = useCallback((institutionId: number) => {
    setInstitutions((prevInstitutions) =>
      prevInstitutions.filter(
        (institution) => institution.institution.id !== institutionId
      )
    );
  }, []);

  const handleDeleteInstitution = useCallback(
    async (institution: InstitutionWithAddresses) => {
      const institutionId = institution.institution.id;
      try {
        const response = await InstitutionsAPI.deleteInstitution(institutionId);

        if (response.statusCode === 200) {
          addToast({
            message: `Barra pesquera eliminada`,
            type: "success",
            location: "bottom-center",
          });

          deleteInstitution(institutionId);
        } else {
          handleError(response.message);
        }
      } catch (error) {
        handleError("Error al eliminar la barra pesquera");
      }
    },
    [addToast, deleteInstitution, handleError]
  );

  return (
    <InstitutionsContext.Provider
      value={{
        institutions,
        getInstitutions,
        error,
        isLoading,
        handleError,
        addToast,
        addInstitution,
        updateInstitution,
        handleDeleteInstitution,
      }}
    >
      {children}
    </InstitutionsContext.Provider>
  );
};

export const useInstitutions = () => {
  const context = useContext(InstitutionsContext);
  if (!context) {
    throw new Error(
      "useInstitutions must be used within a InstitutionsProvider"
    );
  }
  return context;
};
